import { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';

export interface ProgressBarProps {
  /** Valor del progreso (0-100) */
  value: number;
  /** Valor máximo (default: 100) */
  max?: number;
  /** Tamaño del barra */
  size?: 'sm' | 'md' | 'lg';
  /** Variante de color */
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /** Mostrar porcentaje */
  showLabel?: boolean;
  /** Label personalizado */
  label?: string;
  /** Clase CSS adicional */
  className?: string;
  /** Si está indeterminado (animación) */
  indeterminate?: boolean;
}

/**
 * ProgressBar - Barra de progreso con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Track: bg-secondary-100 dark:bg-white/5
 * - Rounded full para suavidad
 * - Animación mejorada
 */
const sizeClasses = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

const variantClasses = {
  primary: 'bg-primary-600 dark:bg-primary-500',
  success: 'bg-success-600 dark:bg-success-500',
  warning: 'bg-warning-600 dark:bg-warning-500',
  danger: 'bg-danger-600 dark:bg-danger-500',
  info: 'bg-info-600 dark:bg-info-500',
};

export const ProgressBar = memo(function ProgressBar({
  value,
  max = 100,
  size = 'md',
  variant = 'primary',
  showLabel = false,
  label,
  className,
  indeterminate = false,
}: ProgressBarProps) {
  // Memoized percentage calculation
  const percentage = useMemo(() => Math.min(Math.max((value / max) * 100, 0), 100), [value, max]);
  const displayLabel = label || (showLabel ? `${Math.round(percentage)}%` : null);

  return (
    <div className={cn('w-full', className)}>
      {displayLabel && (
        <div className="flex justify-between text-sm text-secondary-600 dark:text-secondary-400 mb-1">
          <span>{displayLabel}</span>
        </div>
      )}
      <div
        className={cn(
          'w-full rounded-full overflow-hidden',
          // Light mode
          'bg-secondary-100',
          // Dark mode - Tailwind Plus style
          'dark:bg-white/5',
          sizeClasses[size]
        )}
      >
        <div
          className={cn(
            'h-full rounded-full',
            'transition-all duration-300 ease-out',
            variantClasses[variant],
            indeterminate && 'animate-pulse'
          )}
          style={{
            width: indeterminate ? '100%' : `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
});
